import React, { useState } from "react";
import { Card, CardBody, Button, Input, Label, Badge, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import useFbTokens from "../../Hooks/useFbTokens";

const PERMISSIONS_LIST = [
  { id: "pages_show_list", title: "pages_show_list", desc: "Access Facebook Pages", defaultChecked: true, color: "#F97316", icon: "mdi-flag-variant-outline" },
  { id: "pages_read_engagement", title: "pages_read_engagement", desc: "Read content & page metrics", defaultChecked: true, color: "#06B6D4", icon: "mdi-file-document-outline" },
  { id: "leads_retrieval", title: "leads_retrieval", desc: "Access leads from Facebook Ads", defaultChecked: true, color: "#10B981", icon: "mdi-account-group-outline" },
  { id: "whatsapp_business_messaging", title: "whatsapp_business_messaging", desc: "Send & manage WhatsApp messages", defaultChecked: false, color: "#22C55E", icon: "mdi-whatsapp" },
  { id: "pages_manage_metadata", title: "pages_manage_metadata", desc: "Manage page settings & metadata", defaultChecked: false, color: "#3B82F6", icon: "mdi-cog-outline" },
  { id: "pages_manage_ads", title: "pages_manage_ads", desc: "Manage ads for your pages", defaultChecked: false, color: "#EC4899", icon: "mdi-bullhorn-outline" },
  { id: "business_management", title: "business_management", desc: "Access business assets", defaultChecked: false, color: "#F59E0B", icon: "mdi-chart-bar" },
];

const FbLoginPage = () => {
  const { autoConnect, getOAuthUrl, handleOAuthCallback, loading: tokenLoading } = useFbTokens();
  const urlCode = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("code") : null;
  const [step, setStep] = useState(urlCode ? 3 : 1);
  const [isProcessingCallback, setIsProcessingCallback] = useState(!!urlCode);
  const [connectedResult, setConnectedResult] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState(
    PERMISSIONS_LIST.filter((p) => p.defaultChecked).map((p) => p.id)
  );
  const [accessToken, setAccessToken] = useState("");
  const [autoSync, setAutoSync] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);

  // Extract real logged-in CRM user details dynamically from localStorage
  const authUserRaw = JSON.parse(localStorage.getItem("authUser"));
  const userObj = authUserRaw?.response || authUserRaw?.user || authUserRaw || {};
  const firstName = userObj?.firstName || userObj?.name || "CRM User";
  const userName = `${firstName} ${userObj?.lastName || ""}`.trim();
  const userEmail = userObj?.email || "user@realtyshopee.in";
  const userInitial = firstName.charAt(0).toUpperCase();

  // Detect OAuth redirect callback code (?code=...) or Meta error redirect
  React.useEffect(() => {
    if (window.location.hash === "#_=_") {
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
    }
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("code");
    const fbErrorMsg = searchParams.get("error_message") || searchParams.get("error_description") || searchParams.get("error");

    if (fbErrorMsg) {
      toast.error(`Facebook OAuth Error: ${decodeURIComponent(fbErrorMsg)}`);
      window.history.replaceState({}, document.title, window.location.pathname);
      setStep(1);
      return;
    }

    if (code) {
      (async () => {
        try {
          setIsProcessingCallback(true);
          setSaving(true);
          const redirectUri = `${window.location.origin}/settings/system/fb-login`;
          const res = await handleOAuthCallback(code, redirectUri);
          setConnectedResult(res?.data || res);
          toast.success(res?.message || "Facebook Account Connected Successfully!");
          setStep(3);
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (err) {
          const errMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "OAuth Authentication Failed";
          toast.error(typeof errMsg === "object" ? (errMsg.message || JSON.stringify(errMsg)) : errMsg);
          setStep(1);
        } finally {
          setSaving(false);
          setIsProcessingCallback(false);
        }
      })();
    }
  }, []);

  const handleFbOAuthRedirect = async () => {
    try {
      setOauthLoading(true);
      const redirectUri = `${window.location.origin}/settings/system/fb-login`;
      const res = await getOAuthUrl({ scopes: selectedPermissions, redirectUri });
      if (res?.data?.oauthUrl) {
        window.location.href = res.data.oauthUrl;
      } else {
        setStep(2);
      }
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Direct OAuth URL fetch failed";
      toast.error(typeof errMsg === "object" ? (errMsg.message || JSON.stringify(errMsg)) : errMsg);
      setStep(2);
    } finally {
      setOauthLoading(false);
    }
  };

  const handleTogglePermission = (id) => {
    setSelectedPermissions((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedPermissions(
      selectedPermissions.length === PERMISSIONS_LIST.length
        ? []
        : PERMISSIONS_LIST.map((p) => p.id)
    );
  };

  const handleCopyToken = () => {
    if (!accessToken) return;
    navigator.clipboard.writeText(accessToken);
    setCopied(true);
    toast.info("Access token copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUseThisAccount = async () => {
    if (!accessToken.trim()) {
      toast.error("Please paste an Access Token to complete setup");
      return;
    }
    try {
      setSaving(true);
      const res = await autoConnect(accessToken.trim());
      setConnectedResult(res?.data || res);
      toast.success(res?.message || "Facebook Account Connected Successfully!");
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Connection Failed";
      toast.error(typeof errMsg === "object" ? (errMsg.message || JSON.stringify(errMsg)) : errMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content pb-2" style={{ backgroundColor: "#F8FAFC", minHeight: "calc(100vh - 75px)" }}>
      <Breadcrumbs title="Settings" breadcrumbItem="FB Login & OAuth Setup" />

      {/* 🔹 STEPPER PROGRESS BAR (Compact single-line) */}
      <div className="row justify-content-center mb-2">
        <div className="col-12 col-xl-10">
          <Card className="border-0 shadow-sm rounded-3">
            <CardBody className="py-2 px-4">
              <div className="d-flex align-items-center justify-content-between">
                {/* Step 1 */}
                <div className="d-flex align-items-center gap-2 cursor-pointer" onClick={() => setStep(1)}>
                  <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold fs-13" style={{ width: 26, height: 26, backgroundColor: step >= 1 ? "#1877F2" : "#CBD5E1", color: "#FFF" }}>
                    {step > 1 ? "✓" : "1"}
                  </div>
                  <span className="fs-13" style={{ fontWeight: step === 1 ? "700" : "500", color: step === 1 ? "#1877F2" : "#64748B" }}>Connect Account</span>
                </div>

                <div className="flex-grow-1 mx-3" style={{ height: 2, backgroundColor: step >= 2 ? "#1877F2" : "#E2E8F0" }} />

                {/* Step 2 */}
                <div className="d-flex align-items-center gap-2 cursor-pointer" onClick={() => setStep(2)}>
                  <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold fs-13" style={{ width: 26, height: 26, backgroundColor: step >= 2 ? "#1877F2" : "#CBD5E1", color: "#FFF" }}>
                    {step > 2 ? "✓" : "2"}
                  </div>
                  <span className="fs-13" style={{ fontWeight: step === 2 ? "700" : "500", color: step === 2 ? "#1877F2" : "#64748B" }}>Select Permissions</span>
                </div>

                <div className="flex-grow-1 mx-3" style={{ height: 2, backgroundColor: step >= 3 ? "#1877F2" : "#E2E8F0" }} />

                {/* Step 3 */}
                <div className="d-flex align-items-center gap-2 cursor-pointer" onClick={() => setStep(3)}>
                  <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold fs-13" style={{ width: 26, height: 26, backgroundColor: step === 3 ? "#1877F2" : "#CBD5E1", color: "#FFF" }}>
                    3
                  </div>
                  <span className="fs-13" style={{ fontWeight: step === 3 ? "700" : "500", color: step === 3 ? "#1877F2" : "#64748B" }}>Get Token & Setup</span>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* 🔹 PROCESSING CALLBACK LOADING SCREEN */}
      {isProcessingCallback && (
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <Card className="border-0 shadow-sm rounded-4 text-center py-4 px-3 bg-white">
              <CardBody className="py-3">
                <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} className="mb-3" />
                <h5 className="fw-bold mb-1" style={{ color: "#0F172A" }}>Connecting Facebook Account...</h5>
                <p className="fs-13 text-muted mb-0">Exchanging Meta OAuth token, importing Facebook pages & activating lead webhooks.</p>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* 🔹 STEP 1: CONNECT ACCOUNT */}
      {!isProcessingCallback && step === 1 && (
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-5">
            <Card className="border-0 shadow-sm rounded-4 text-center py-3 px-4 bg-white">
              <CardBody className="py-2">
                <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2 shadow-sm" style={{ width: 62, height: 62, backgroundColor: "#EFF6FF", color: "#1877F2" }}>
                  <i className="mdi mdi-facebook" style={{ fontSize: 40 }} />
                </div>
                <h5 className="fw-bold mb-1" style={{ color: "#0F172A" }}>Connect Your Facebook Account</h5>
                <p className="mb-3 fs-13" style={{ color: "#64748B" }}>Login with your Facebook account to get access to pages, lead forms and required permissions.</p>

                <div className="text-start rounded-3 p-3 mb-3 border" style={{ backgroundColor: "#F8FAFC", borderColor: "#E2E8F0" }}>
                  <div className="d-flex align-items-center gap-2 mb-2 fs-13" style={{ color: "#334155" }}>
                    <i className="mdi mdi-lock-outline fs-16 text-primary" />
                    <span>Secure login via Facebook OAuth</span>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-2 fs-13" style={{ color: "#334155" }}>
                    <i className="mdi mdi-account-group-outline fs-16 text-primary" />
                    <span>We only request selected permissions</span>
                  </div>
                  <div className="d-flex align-items-center gap-2 fs-13" style={{ color: "#334155" }}>
                    <i className="mdi mdi-shield-check-outline fs-16 text-primary" />
                    <span>Your account credentials are not stored</span>
                  </div>
                </div>

                <Button size="md" className="w-100 py-2 fs-14 fw-bold border-0 rounded-3 shadow-sm" style={{ backgroundColor: "#1877F2", color: "#FFF" }} onClick={() => setStep(2)}>
                  <i className="mdi mdi-facebook me-2 fs-16" /> Login with Facebook &rarr;
                </Button>
                <p className="fs-11 mt-2 mb-0" style={{ color: "#94A3B8" }}>Select permissions and authorize access.</p>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* 🔹 STEP 2: SELECT PERMISSIONS */}
      {!isProcessingCallback && step === 2 && (
        <div className="row justify-content-center">
          <div className="col-12 col-xl-10">
            <Card className="border-0 shadow-sm rounded-4 p-2 bg-white">
              <CardBody className="py-2 px-4">
                {/* User Profile Header */}
                <div className="d-flex align-items-center justify-content-between p-2 px-3 rounded-3 mb-2 border" style={{ backgroundColor: "#F8FAFC", borderColor: "#E2E8F0" }}>
                  <div className="d-flex align-items-center gap-2">
                    <div className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold fs-14 shadow-sm" style={{ width: 34, height: 34, backgroundColor: "#1877F2" }}>
                      {userInitial}
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold fs-14" style={{ color: "#0F172A" }}>{userName}</h6>
                      <small className="fs-12" style={{ color: "#64748B" }}>{userEmail}</small>
                    </div>
                  </div>
                  <Button color="link" className="text-decoration-none fs-12 p-0 fw-bold" style={{ color: "#2563EB" }} onClick={() => setStep(1)}>
                    <i className="mdi mdi-refresh me-1" /> Change Account
                  </Button>
                </div>

                {/* Header title & Select All */}
                <div className="d-flex align-items-center justify-content-between mb-2 pb-1 border-bottom">
                  <div>
                    <h6 className="fw-bold mb-0 fs-14" style={{ color: "#0F172A" }}>Select Required Permissions</h6>
                    <small className="fs-12" style={{ color: "#64748B" }}>Choose the permissions needed to access pages, lead forms and manage Facebook data.</small>
                  </div>
                  <div className="d-flex align-items-center gap-1 cursor-pointer flex-shrink-0 ms-3 text-nowrap" onClick={handleSelectAll}>
                    {selectedPermissions.length === PERMISSIONS_LIST.length ? (
                      <i className="mdi mdi-checkbox-marked-outline text-success fs-18" />
                    ) : (
                      <i className="mdi mdi-checkbox-blank-outline text-muted fs-18" />
                    )}
                    <span className="fs-12 fw-bold text-nowrap" style={{ color: "#334155" }}>Select All</span>
                  </div>
                </div>

                {/* Permissions Grid */}
                <div className="row g-2 mb-2">
                  {PERMISSIONS_LIST.map((perm) => {
                    const isChecked = selectedPermissions.includes(perm.id);
                    return (
                      <div className="col-12 col-md-6" key={perm.id}>
                        <div
                          className="d-flex align-items-center justify-content-between p-2 px-3 rounded-3 cursor-pointer transition-all border"
                          style={{
                            backgroundColor: isChecked ? "#F0FDF4" : "#FFFFFF",
                            borderColor: isChecked ? "#10B981" : "#E2E8F0",
                            borderWidth: isChecked ? "1.5px" : "1px",
                            minHeight: 48,
                          }}
                          onClick={() => handleTogglePermission(perm.id)}
                        >
                          <div className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1 me-2">
                            <div className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 30, height: 30, backgroundColor: `${perm.color}18`, color: perm.color }}>
                              <i className={`mdi ${perm.icon} fs-16`} />
                            </div>
                            <div className="overflow-hidden">
                              <span className="fw-bold d-block fs-13 text-truncate" style={{ color: "#0F172A" }}>{perm.title}</span>
                              <small className="fs-11 text-muted d-block text-truncate">{perm.desc}</small>
                            </div>
                          </div>
                          <i className={`mdi ${isChecked ? "mdi-checkbox-marked-outline text-success" : "mdi-checkbox-blank-outline text-muted"} fs-18 flex-shrink-0 ms-1`} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer Buttons */}
                <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                  <Button size="sm" className="px-3 border-0 fw-bold" style={{ backgroundColor: "#F1F5F9", color: "#475569" }} onClick={() => setStep(1)}>
                    &larr; Back
                  </Button>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      className="px-3 fw-bold border rounded-3"
                      style={{ backgroundColor: "#FFFFFF", color: "#334155", borderColor: "#CBD5E1" }}
                      onClick={() => {
                        if (selectedPermissions.length === 0) return toast.error("Please select at least one permission");
                        setStep(3);
                      }}
                    >
                      Paste Token Manually &rarr;
                    </Button>
                    <Button
                      size="sm"
                      className="px-3 fw-bold border-0 rounded-3 shadow-sm"
                      style={{ backgroundColor: "#1877F2", color: "#FFF" }}
                      disabled={oauthLoading || saving}
                      onClick={() => {
                        if (selectedPermissions.length === 0) return toast.error("Please select at least one permission");
                        handleFbOAuthRedirect();
                      }}
                    >
                      {oauthLoading ? (
                        <><Spinner size="sm" className="me-2" /> Connecting...</>
                      ) : (
                        <><i className="mdi mdi-facebook me-1" /> Login with Meta OAuth &rarr;</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* 🔹 STEP 3: GET TOKEN & SETUP (2-Column Ultra Sleek Layout - ZERO SCROLLING!) */}
      {!isProcessingCallback && step === 3 && (
        <div className="row justify-content-center">
          <div className="col-12 col-xl-10">
            <Card className="border-0 shadow-sm rounded-4 p-2 bg-white">
              <CardBody className="py-2 px-4">
                {connectedResult ? (
                  <div className="row g-3">
                    {/* LEFT COLUMN: Account Profile + Auto Sync + Actions */}
                    <div className="col-12 col-md-5 d-flex flex-column justify-content-between">
                      <div>
                        {/* Connected Alert */}
                        <div className="d-flex align-items-center gap-2 mb-2 p-2 px-3 rounded-3 border" style={{ backgroundColor: "#ECFDF5", borderColor: "#A7F3D0" }}>
                          <i className="mdi mdi-check-circle text-success fs-20 flex-shrink-0" />
                          <div>
                            <h6 className="mb-0 fw-bold fs-13" style={{ color: "#065F46" }}>Facebook Connected!</h6>
                            <small className="fs-11" style={{ color: "#047857" }}>{connectedResult.message || "Tokens active in MongoDB."}</small>
                          </div>
                        </div>

                        {/* Profile Info */}
                        <div className="p-2 px-3 rounded-3 mb-2 border" style={{ backgroundColor: "#F8FAFC", borderColor: "#E2E8F0" }}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-2">
                              <div className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold fs-14 shadow-sm" style={{ width: 38, height: 38, backgroundColor: "#1877F2" }}>
                                {(connectedResult.user?.name || userName).charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <h6 className="mb-0 fw-bold fs-14" style={{ color: "#0F172A" }}>{connectedResult.user?.name || userName}</h6>
                                <small className="fs-11 text-muted">ID: {connectedResult.user?.refId || connectedResult.user?.id || "Connected"}</small>
                              </div>
                            </div>
                            <Badge className="px-2 py-1 border-0 rounded-pill bg-success-subtle text-success fw-bold fs-11">
                              ✓ Saved
                            </Badge>
                          </div>
                        </div>

                        {/* Sleek Custom Toggle */}
                        <div className="d-flex align-items-center justify-content-between p-2 px-3 rounded-3 mb-2 border" style={{ backgroundColor: "#F8FAFC", borderColor: "#E2E8F0" }}>
                          <div>
                            <span className="fw-bold d-block fs-13" style={{ color: "#0F172A" }}>Auto Sync to CRM</span>
                            <small className="fs-11" style={{ color: "#64748B" }}>Sync leads from FB forms to CRM.</small>
                          </div>
                          <div
                            className="cursor-pointer d-flex align-items-center p-1 rounded-pill transition-all flex-shrink-0"
                            style={{
                              width: 44,
                              height: 22,
                              backgroundColor: autoSync ? "#10B981" : "#CBD5E1",
                              transition: "background-color 0.2s ease",
                            }}
                            onClick={() => setAutoSync(!autoSync)}
                          >
                            <div
                              className="rounded-circle bg-white shadow-sm"
                              style={{
                                width: 14,
                                height: 14,
                                transform: autoSync ? "translateX(22px)" : "translateX(2px)",
                                transition: "transform 0.2s ease",
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Left Column Action Buttons */}
                      <div className="d-flex flex-column gap-2 pt-2 border-top mt-2">
                        <Button size="sm" className="w-100 fw-bold border-0 py-2 rounded-3 shadow-sm" style={{ backgroundColor: "#1877F2", color: "#FFF" }} onClick={() => window.location.href = "/settings/system/token-manager"}>
                          Manage Tokens & Pages &rarr;
                        </Button>
                        <Button size="sm" className="w-100 border-0 fw-bold py-1" style={{ backgroundColor: "#F1F5F9", color: "#475569" }} onClick={() => { setConnectedResult(null); setStep(1); }}>
                          &larr; Connect Another Account
                        </Button>
                      </div>
                    </div>

                    {/* RIGHT COLUMN: Imported Facebook Pages Grid */}
                    <div className="col-12 col-md-7 border-start ps-md-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <Label className="fw-bold fs-13 mb-0" style={{ color: "#0F172A" }}>
                          Imported Facebook Pages ({connectedResult.pages?.length || 0})
                        </Label>
                        <Badge className="bg-primary-subtle text-primary border-0 px-2 py-1 fs-11">
                          Auto-Configured
                        </Badge>
                      </div>

                      {connectedResult.pages && connectedResult.pages.length > 0 ? (
                        <div className="d-flex flex-column gap-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                          {connectedResult.pages.map((pg) => (
                            <div key={pg.id} className="d-flex align-items-center justify-content-between p-2 px-3 rounded-3 border bg-white shadow-sm">
                              <div className="d-flex align-items-center gap-2 overflow-hidden">
                                <i className="mdi mdi-flag-variant-outline text-primary fs-18 flex-shrink-0" />
                                <span className="fw-bold fs-13 text-truncate" style={{ color: "#334155" }}>{pg.name}</span>
                              </div>
                              <Badge className="bg-success-subtle text-success border-0 px-2 py-1 fs-11 flex-shrink-0 ms-2">
                                ✓ Webhook Active
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center p-3 text-muted border rounded-3 bg-light fs-12">
                          No Facebook pages associated with this account.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center gap-2 border-0 mb-3 p-2 rounded-3" style={{ backgroundColor: "#ECFDF5", border: "1px solid #A7F3D0" }}>
                      <i className="mdi mdi-check-circle text-success fs-20" />
                      <div>
                        <h6 className="mb-0 fw-bold fs-13" style={{ color: "#065F46" }}>Permissions Granted Successfully!</h6>
                        <small className="fs-11" style={{ color: "#047857" }}>Your Facebook account is connected and permissions are approved.</small>
                      </div>
                    </div>

                    {/* Access Token Box */}
                    <div className="mb-3">
                      <Label className="fw-bold fs-13 mb-1" style={{ color: "#0F172A" }}>Access Token</Label>
                      <div className="position-relative">
                        <Input
                          type="textarea"
                          rows="3"
                          placeholder="Paste Facebook Access Token here..."
                          value={accessToken}
                          onChange={(e) => setAccessToken(e.target.value)}
                          className="pe-5 fs-12 font-monospace border rounded-3"
                          style={{ backgroundColor: "#F8FAFC", borderColor: "#CBD5E1" }}
                        />
                        <Button size="sm" className="position-absolute top-0 end-0 m-1 border fw-bold fs-11" style={{ backgroundColor: "#FFF", color: "#334155" }} onClick={handleCopyToken} disabled={!accessToken}>
                          {copied ? "Copied! ✓" : "📋 Copy"}
                        </Button>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mt-1 fs-11">
                        <span style={{ color: "#64748B" }}>ℹ️ This token will be used to automatically sync leads to your CRM.</span>
                        <Badge className="px-2 py-1 border-0" style={{ backgroundColor: "#EFF6FF", color: "#1D4ED8", fontWeight: "600" }}>🔵 Token expires in 60 days</Badge>
                      </div>
                    </div>

                    {/* Dropdowns */}
                    <div className="row g-2 mb-3">
                      <div className="col-md-6">
                        <Label className="fw-bold fs-13 mb-1" style={{ color: "#0F172A" }}>Meta App</Label>
                        <Input type="select" defaultValue="app1" className="fs-13 border rounded-3 py-1">
                          <option value="app1">App 1 (Pioneer CRM)</option>
                        </Input>
                      </div>
                      <div className="col-md-6">
                        <Label className="fw-bold fs-13 mb-1" style={{ color: "#0F172A" }}>User or Page</Label>
                        <Input type="select" defaultValue="user" className="fs-13 border rounded-3 py-1">
                          <option value="user">User Token ({userName})</option>
                        </Input>
                      </div>
                    </div>

                    {/* Custom Sleek Toggle */}
                    <div className="d-flex align-items-center justify-content-between p-3 rounded-3 mb-3 border" style={{ backgroundColor: "#F8FAFC", borderColor: "#E2E8F0" }}>
                      <div>
                        <span className="fw-bold d-block fs-13" style={{ color: "#0F172A" }}>Auto Sync to CRM</span>
                        <small className="fs-11" style={{ color: "#64748B" }}>Automatically sync leads from Facebook Lead Forms to CRM.</small>
                      </div>
                      <div
                        className="cursor-pointer d-flex align-items-center p-1 rounded-pill transition-all"
                        style={{
                          width: 44,
                          height: 22,
                          backgroundColor: autoSync ? "#10B981" : "#CBD5E1",
                          transition: "background-color 0.2s ease",
                        }}
                        onClick={() => setAutoSync(!autoSync)}
                      >
                        <div
                          className="rounded-circle bg-white shadow-sm"
                          style={{
                            width: 14,
                            height: 14,
                            transform: autoSync ? "translateX(22px)" : "translateX(2px)",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </div>
                    </div>

                    {/* Setup Complete Banner */}
                    <div className="d-flex align-items-center gap-2 mb-3 p-2 rounded-3 border" style={{ backgroundColor: "#ECFDF5", borderColor: "#A7F3D0" }}>
                      <i className="mdi mdi-check-circle text-success fs-20" />
                      <div>
                        <span className="fw-bold d-block fs-13" style={{ color: "#065F46" }}>Setup complete!</span>
                        <small className="fs-11" style={{ color: "#047857" }}>Leads will be synced automatically based on your settings.</small>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-between pt-2 border-top">
                      <Button size="sm" className="px-3 border-0 fw-bold" style={{ backgroundColor: "#F1F5F9", color: "#475569" }} onClick={() => setStep(2)}>
                        &larr; Back
                      </Button>
                      <Button size="md" className="px-4 fw-bold border-0 rounded-3 shadow-sm" style={{ backgroundColor: "#10B981", color: "#FFF" }} disabled={saving || tokenLoading || !accessToken.trim()} onClick={handleUseThisAccount}>
                        {saving || tokenLoading ? (
                          <><Spinner size="sm" className="me-2" />Saving & Syncing Pages...</>
                        ) : (
                          <>Use This Account &rarr;</>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default FbLoginPage;
